import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Search, Tag, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { brandsAPI, manufacturersAPI } from '@/services/api';

const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [formData, setFormData] = useState({ code: '', name: '', description: '', manufacturer_id: '' });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchBrands = useCallback(async () => {
    try {
      const response = await brandsAPI.list({ search });
      setBrands(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch brands', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  const fetchManufacturers = useCallback(async () => {
    try {
      const response = await manufacturersAPI.dropdown();
      setManufacturers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch manufacturers:', error);
    }
  }, []);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);
  useEffect(() => { fetchManufacturers(); }, [fetchManufacturers]);

  const handleOpenDialog = async (brand = null) => {
    if (brand) {
      setSelectedBrand(brand);
      setFormData({ 
        code: brand.code, 
        name: brand.name, 
        description: brand.description || '',
        manufacturer_id: brand.manufacturer_id || ''
      });
    } else {
      setSelectedBrand(null);
      // Auto-generate code for new brand
      try {
        const nextCode = await brandsAPI.getNextCode();
        setFormData({ code: nextCode.code, name: '', description: '', manufacturer_id: '' });
      } catch (error) {
        console.error('Error getting next code:', error);
        setFormData({ code: '', name: '', description: '', manufacturer_id: '' });
      }
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast({ title: 'Validation Error', description: 'Name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (selectedBrand) {
        await brandsAPI.update(selectedBrand.id, formData);
        toast({ title: 'Success', description: 'Brand updated successfully' });
      } else {
        await brandsAPI.create(formData);
        toast({ title: 'Success', description: 'Brand created successfully' });
      }
      setDialogOpen(false);
      fetchBrands();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save brand', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await brandsAPI.delete(selectedBrand.id);
      toast({ title: 'Success', description: 'Brand deactivated successfully' });
      setDeleteDialogOpen(false);
      fetchBrands();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete brand', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <Toaster />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Brands</h1>
          <p className="text-gray-500">Manage product brands</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-blue-500 hover:bg-blue-600" data-testid="add-brand-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Brand
        </Button>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input placeholder="Search brands..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-200 text-gray-800" />
        </div>
      </div>

      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : brands.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No brands found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="text-gray-500">Code</TableHead>
                  <TableHead className="text-gray-500">Name</TableHead>
                  <TableHead className="text-gray-500">Manufacturer</TableHead>
                  <TableHead className="text-gray-500">Description</TableHead>
                  <TableHead className="text-gray-500">Status</TableHead>
                  <TableHead className="text-gray-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brands.map(brand => {
                  const manufacturer = manufacturers.find(m => m.id === brand.manufacturer_id);
                  return (
                    <TableRow key={brand.id} className="border-gray-200 hover:bg-white" data-testid={`brand-row-${brand.code}`}>
                      <TableCell className="font-medium text-gray-800">{brand.code}</TableCell>
                      <TableCell className="text-gray-600">{brand.name}</TableCell>
                      <TableCell>
                        {manufacturer ? (
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-indigo-500" />
                            <span className="text-gray-700">{manufacturer.company_name}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-500 max-w-xs truncate">{brand.description || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={brand.is_active ? 'default' : 'secondary'}>
                          {brand.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                            onClick={() => handleOpenDialog(brand)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => { setSelectedBrand(brand); setDeleteDialogOpen(true); }}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800">
          <DialogHeader>
            <DialogTitle>{selectedBrand ? 'Edit Brand' : 'Add Brand'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-600">Code *</Label>
                <Input value={formData.code} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., NIKE" className="bg-gray-50 border-gray-300" disabled={!!selectedBrand} data-testid="brand-code-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600">Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Brand Name" className="bg-gray-50 border-gray-300" data-testid="brand-name-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600 flex items-center gap-2">
                <Building className="w-4 h-4 text-indigo-500" />
                Manufacturer / Importer
              </Label>
              <Select 
                value={formData.manufacturer_id || "none"} 
                onValueChange={(v) => setFormData(p => ({ ...p, manufacturer_id: v === "none" ? "" : v }))}
              >
                <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="manufacturer-select">
                  <SelectValue placeholder="Select manufacturer" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 z-[9999]">
                  <SelectItem value="none">None</SelectItem>
                  {manufacturers.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.company_name}
                      {m.type && <span className="text-gray-400 ml-1">({m.type})</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                The manufacturer will be charged Brand Registration Fee when this brand is created.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600">Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Brand description" className="bg-gray-50 border-gray-300" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300 text-gray-600">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-500 hover:bg-blue-600" data-testid="save-brand-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Deactivate Brand?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will deactivate the brand "{selectedBrand?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BrandsPage;
